import { useEffect, useState } from 'react';
import { useDataStore } from '../store/dataStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  MapPin,
  Phone,
  User,
  Trash2,
  Loader2,
  Building
} from 'lucide-react';

export default function VenuesPage() {
  const {
    venues,
    venuesLoading,
    fetchVenues,
    addVenue,
    deleteVenue
  } = useDataStore();

  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    phone: '',
    contact_person: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Lütfen salon adını girin');
      return;
    }

    setIsSubmitting(true);
    try {
      await addVenue(formData);
      toast.success('Mekan eklendi');
      setIsOpen(false);
      setFormData({
        name: '',
        address: '',
        city: '',
        phone: '',
        contact_person: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`"${name}" mekanını silmek istediğinize emin misiniz?`)) {
      try {
        await deleteVenue(id);
        toast.success('Mekan silindi');
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="venues-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Mekanlar</h1>
          <p className="text-muted-foreground mt-1">Düğün salonlarını yönetin</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-venue-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Mekan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Mekan Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Salon Adı *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Salon adı"
                  data-testid="venue-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Adres</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Tam adres"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Şehir</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Şehir"
                />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="0XXX XXX XX XX"
                />
              </div>
              <div className="space-y-2">
                <Label>İlgili Kişi</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="Yetkili kişi adı"
                />
              </div>
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar..."
                  rows={2}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1" data-testid="venue-submit-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Venues Grid */}
      {venuesLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : venues.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {venues.map((venue, index) => (
            <Card key={venue.id} className={`animate-fade-in stagger-${(index % 5) + 1}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Building className="h-6 w-6 text-primary" />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(venue.id, venue.name)}
                    className="text-destructive hover:text-destructive h-8 w-8"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <h3 className="font-semibold text-lg mb-1">{venue.name}</h3>
                {venue.city && (
                  <p className="text-sm text-primary font-medium mb-3">{venue.city}</p>
                )}
                <div className="space-y-2 text-sm text-muted-foreground">
                  {venue.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{venue.address}</span>
                    </div>
                  )}
                  {venue.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{venue.phone}</span>
                    </div>
                  )}
                  {venue.contact_person && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{venue.contact_person}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="text-lg font-medium mb-2">Henüz mekan yok</h3>
          <p className="text-muted-foreground mb-4">Yeni mekan ekleyerek başlayın</p>
        </div>
      )}
    </div>
  );
}
